// Pinia store migration for Nuxt 4
import { defineStore } from 'pinia';
import { useCookie } from 'nuxt/app';
import Info from '../services/info.service';
import Search from '../services/search.service';
import Subcategory from '../services/subcategory.service';
import SubcategoryTab from '../services/subcategoryTab.service';
import ServerInit from '../services/serverInit.service';
import { saveRedirectLocation } from '../utils/redirectLocation';

// Type definitions
interface SearchResults {
  totalCount: number;
  items: any[];
}

interface LangJson {
  da: Record<string, any>;
  de: Record<string, any>;
  sv: Record<string, any>;
  no: Record<string, any>;
  en: Record<string, any>;
  'en-gb': Record<string, any>;
  global: Record<string, any>;
  [key: string]: Record<string, any>; // Allow string indexing
}

interface ScrollLockState {
  isLocked: boolean;
  originalScrollY: number;
  originalHtmlOverflow: string;
  originalBodyOverflow: string;
  originalBodyPaddingRight: string;
  originalNavigationPaddingRight: string;
  lockCount: number;
  isScrollOperationInProgress: boolean;
}

interface MainCategories {
  products: string;
  info: string;
  downloads: string;
}

interface Category {
  nodeId: string | number;
  id: string | number;
  nodes?: Category[];
  external?: boolean | { title?: string };
  title?: string;
  productBlockType?: string;
  resources?: Array<{ mimeType?: string; fileName?: string; [key: string]: any }>;
  publishedTo?: string;
}

interface SearchPayload {
  items?: any[];
  totalCount?: number;
  q?: string;
  token?: string;
  offset?: number;
  amount?: number;
  append?: boolean;
}

interface RouteParams {
  category?: string | number;
  subcategory?: string | number;
  subcategoryTab?: string | number;
  token?: string;
}

interface InfoPayload {
  params: RouteParams;
  routeName?: string;
}

interface SubcategoryPayload {
  params: RouteParams;
}

interface SubcategoryTabPayload {
  params: RouteParams & { subcategoryTab?: string };
  checkData?: { check: boolean };
}

interface LoadMoreSearchPayload {
  q?: string;
  token?: string;
  offset?: number;
  amount?: number;
}

interface ServerInitContext {
  req?: { headers?: { host?: string } };
  route?: { query?: { token?: string } };
  params?: RouteParams;
  error?: (err: any) => void;
}

interface MainStoreState {
  baseUrl: string;
  baseUrlApi: string;
  baseUrlMedia: string;
  categories: Category[];
  subcategoryData: any;
  subcategoryTabData: any[];
  downloadsPageData: any[];
  channelNodes: string;
  token: string;
  lang: string;
  isGlobal: boolean;
  isTest: boolean;
  isGlobalDataLoaded: boolean;
  hasToken: boolean;
  infoPageData: any[];
  searchResults: SearchResults;
  langJson: LangJson;
  sidebarState: number;
  searchFieldState: any;
  tabChangeState: boolean;
  sidebarChangeState: boolean;
  isInternal: boolean;
  /**
   * The token that produced the current `isInternal` answer (null = never asked).
   * `isInternal` is a pure function of the token we present, so this lets callers
   * tell "not internal" apart from "not asked with this token yet" without
   * re-authenticating to find out.
   */
  statusToken: string | null;
  isErrorMessageOpen: boolean;
  breadcrumbs: any[];
  productDetailsState: boolean;
  resource: any;
  scrollLockState: ScrollLockState;
  mainCategories: MainCategories;
  $msal?: any; // MSAL instance from plugin
}

let tabChangeResetTimer: ReturnType<typeof setTimeout> | null = null;

function readTokenCookie(): string {
  if (typeof document === 'undefined') return '';
  const m = document.cookie.match(/(?:^|;\s*)token=([^;]+)/);
  return m ? decodeURIComponent(m[1]) : '';
}

// No manual Pinia setup needed in Nuxt 4
const InfoService = new Info();
const SearchService = new Search();
const SubcategoryService = new Subcategory();
const SubcategoryTabService = new SubcategoryTab();
const useMainStore = defineStore('main', {
  state: (): MainStoreState => ({
    baseUrl: '/',
    baseUrlApi: '/api/',
    baseUrlMedia: '//cdn.velfac.com/files/',
    categories: [],
    subcategoryData: {},
    subcategoryTabData: [],
    downloadsPageData: [],
    channelNodes: '',
    token: '',
    lang: 'da',
    isGlobal: false,
    isTest: false,
    isGlobalDataLoaded: false,
    hasToken: false,
    infoPageData: [],
    searchResults: { totalCount: 0, items: [] },
    langJson: {
      da: {},
      de: {},
      sv: {},
      no: {},
      en: {},
      'en-gb': {},
      global: {}
    },
    sidebarState: 0,
    searchFieldState: null,
    tabChangeState: false,
    sidebarChangeState: false,
    isInternal: false,
    statusToken: null,
    isErrorMessageOpen: false,
    breadcrumbs: [],
    productDetailsState: false,
    resource: null,
    // Scroll lock state for modals/overlays
    scrollLockState: {
      isLocked: false,
      originalScrollY: 0,
      originalHtmlOverflow: '',
      originalBodyOverflow: '',
      originalBodyPaddingRight: '',
      originalNavigationPaddingRight: '', // Track navigation padding
      lockCount: 0, // Track multiple locks to prevent conflicts
      isScrollOperationInProgress: false // Track active scroll operations
    },
    mainCategories: {
      products: '/products',
      info: '/info',
      downloads: '/downloads'
    },
    $msal: undefined
  }),
  getters: {
    baseUrlGetter: (state) => state.baseUrl,
    baseUrlApiGetter: (state) => {
      let baseUrlApi = state.baseUrlApi;
      if (typeof window === 'undefined' && !baseUrlApi.includes('http')) {
        baseUrlApi = 'http://localhost:8080' + baseUrlApi;
      }
      return baseUrlApi;
    },
    baseUrlMediaGetter: (state) => state.baseUrlMedia
    // ...migrate other getters as needed
  },
  actions: {
    /**
     * Fetch or set search results.
     * If payload has { items }, it sets directly; otherwise expects { q, token } and fetches.
     */
    async setSearchResults(payload: SearchPayload | string = {}) {
      // Direct set path
      if (payload && typeof payload === 'object' && Array.isArray((payload as SearchPayload).items)) {
        this.searchResults = payload as SearchResults;
        return;
      }
      const q = typeof payload === 'string' ? payload : (payload as SearchPayload)?.q;
      if (!q) {
        this.searchResults = { totalCount: 0, items: [] };
        return;
      }
      // Ensure token
      let token = (payload as SearchPayload)?.token || this.token || '';
      if (!token && typeof document !== 'undefined') {
        const m = document.cookie.match(/(?:^|;\s*)token=([^;]+)/);
        token = m ? decodeURIComponent(m[1]) : '';
      }
      try {
        const payloadObj = typeof payload === 'string' ? { q: payload } : (payload as SearchPayload);
        const offset = Number.isInteger(payloadObj?.offset) ? payloadObj.offset! : 0;
        const amount = Number.isInteger(payloadObj?.amount) ? payloadObj.amount! : 50;
        const append = !!payloadObj?.append;
        const svc = new Search(this, { q, token, offset, amount });
        const data = await svc.getSearchResults();
        const next = (data as any)?.data ?? data ?? { totalCount: 0, items: [] };
        if (append && Array.isArray(this.searchResults?.items)) {
          const merged = {
            totalCount: next.totalCount ?? this.searchResults.totalCount ?? 0,
            items: [...this.searchResults.items, ...(next.items || [])]
          };
          this.searchResults = merged;
        } else {
          this.searchResults = next;
        }
      } catch (e) {
        console.error('setSearchResults failed', e);
        this.searchResults = { totalCount: 0, items: [] };
      }
    },
    async loadMoreSearchResults(payload: LoadMoreSearchPayload = {}) {
      // Convenience wrapper to always append
      return await this.setSearchResults({ ...payload, append: true });
    },
    /**
     * Load info page data by category (node id) into infoPageData.
     * Mirrors Nuxt 2: on info-category routes, use first child node's nodeId.
     * @param {{ params: any, routeName?: string }} payload
     */
    async setInfoPageData(payload: InfoPayload = { params: {} }) {
      const { params, routeName } = payload;
      if (!params || !params.category) {
        this.infoPageData = [];
        return;
      }
      // Resolve token from store/cookie
      let token = this.token || '';
      if (!token && typeof document !== 'undefined') {
        const m = document.cookie.match(/(?:^|;\s*)token=([^;]+)/);
        token = m ? decodeURIComponent(m[1]) : '';
      }
      // Derive the proper node id (Nuxt 2 behavior)
      let targetId = params.category;
      const isInfoCategoryRoute = !!(routeName && String(routeName).includes('info-category'));
      if (isInfoCategoryRoute && Array.isArray(this.categories) && this.categories.length) {
        const cat = this.categories.find((c) => String(c.nodeId) === String(params.category) || String(c.id) === String(params.category));
        const first = cat && Array.isArray(cat.nodes) && cat.nodes.length ? cat.nodes[0] : null;
        if (first && (first.nodeId || first.id)) targetId = first.nodeId ?? first.id;
      }
      try {
        const infoSvc = new Info(this, { token }, targetId);
        const data = await infoSvc.getInfoPage();
        this.infoPageData = Array.isArray(data) ? data : (data?.data ?? []);
      } catch (e) {
        console.error('setInfoPageData failed', e);
        this.infoPageData = [];
      }
    },
    /**
     * Server-side initialization: loads translations, status, and categories.
     * @param {Object} context - { req, route, params, error }
     */
    setSidebarChangeState(val: boolean) {
      this.sidebarChangeState = !!val;
    },
    setSidebarState(val: number) {
      this.sidebarState = Number(val) || 0;
    },
    setSearchFieldState(val: any) {
      this.searchFieldState = val;
    },
    async serverInit(context: ServerInitContext = {}) {
      const { req = {}, route = {}, params = {}, error = () => {} } = context;
      let token = '';
      let host = '';
      if (route && route.query && route.query.token) {
        token = route.query.token;
      } else if (typeof useCookie === 'function') {
        // Nuxt composable for cookies
        token = useCookie('token').value || '';
      }
      // persist token for later actions (avoids using composables outside app context)
      this.token = token;
      this.hasToken = !!token;
      if (req && req.headers && req.headers.host) {
        host = req.headers.host;
      } else if (typeof window !== 'undefined') {
        host = window.location.host;
      }
      let lang = this.lang;
      if (host.includes('dovista-pim-dk') || host.includes('produkter.velfac.dk') || host.includes('produkter-test.velfac.dk')) {
        lang = 'da';
      } else if (host.includes('dovista-pim-de') || host.includes('produkte.velfac.de') || host.includes('produkte-test.velfac.de')) {
        lang = 'de';
      } else if (
        host.includes('sf-') ||
        host.includes('svenskafonster') ||
        host.includes('dovista-pim-se') ||
        host.includes('produkter.velfac.se') ||
        host.includes('produkter-test.velfac.se')
      ) {
        if (host.includes('global')) {
          lang = 'global';
        } else {
          lang = 'sv';
        }
      } else if (host.includes('natre') || host.includes('dovista-pim-no') || host.includes('velfac.no')) {
        if (host.includes('produkter.natre.no') || host.includes('global')) {
          lang = 'global';
        } else {
          lang = 'no';
        }
      } else if (
        host.includes('dovista-pim-en') ||
        host.includes('products-domestic.velfac.co.uk') ||
        host.includes('products-domestic-test.velfac.co.uk')
      ) {
        lang = 'en';
      } else if (
        host.includes('dovista-pim-gb') ||
        host.includes('products-commercial.velfac.co.uk') ||
        host.includes('products-commercial-test.velfac.co.uk')
      ) {
        lang = 'en-gb';
      } else if (host.includes('rationel') || host.includes('productdb.rationel.dk')) {
        lang = 'global';
      } else if (host.includes('global') || host.includes('productdb.velfac.dk')) {
        lang = 'global';
      }

      if (lang != 'global') {
        this.isGlobalDataLoaded = true;
      }

      // Create ServerInitService instance in context
      const ServerInitService = new ServerInit(this);
      ServerInitService.setToken(token);

      try {
        // Load translations
        const translations = await ServerInitService.getTranslations();
        this.langJson = translations as LangJson;
        this.lang = lang;
        this.isGlobal = lang === 'global';
        this.isTest = host.includes('test');
      } catch (err) {
        error?.(err);
      }
      const msal = (window as any)?.msal;
      try {
        // Load status
        const status = await ServerInitService.getStatus();
        this.isInternal = status.isInternal;
        this.statusToken = token;
        if (lang == 'global' && this.isInternal) {
          this.isGlobalDataLoaded = true;
        }
      } catch (err) {
        error?.(err);
      }
      try {
        // Load categories
        await this.loadCategories({ params });
      } catch (err) {
        error?.(err);
      }

      try {
        // A global channel is internal-only, so an anonymous visitor has to sign in.
        // The *only* thing that justifies sending someone to AAD is the absence of a
        // session. `isInternal === false` must never do it: that is the API's
        // authorization verdict on a token we already hold, so a second visit to AAD
        // — which returns immediately when the AAD session is still alive — comes
        // back with the same token, gets the same verdict, and redirects again. That
        // was the endless login loop on `lang === 'global'`; the `!hasIdToken` test
        // that used to guard it could never break the cycle, because msal-browser v3
        // returns `#code=…` (auth-code + PKCE, never `#id_token=…`) and the msal
        // plugin strips the hash with history.replaceState before this code runs.
        //
        // A stale `isInternal === false` left over from a token-less SSR pass is
        // repaired by refreshStatus() with the real token — see clientInit — not by
        // logging in again.
        if (lang == 'global' && msal && !msal.isAuthenticated()) {
          saveRedirectLocation();
          msal.loginRedirect({ scopes: ['user.read'] });
        }
      } catch (err) {
        error?.(err);
      }
    },
    /**
     * Client-side initialization: runs on the browser after hydration.
     *
     * Its job is to make sure the app's view of the session matches reality — it is
     * NOT an authorization gate. Two distinct states are handled separately:
     *
     *  - a usable token exists, but `isInternal` was decided during a token-less SSR
     *    pass: re-ask the API with the token. That is the only correct repair; the
     *    old code sent these users to AAD instead, which returns instantly on a live
     *    session and produces an endless login loop.
     *  - the token is gone or expired while MSAL still has the account: renew it
     *    silently, and only fall back to an interactive redirect when that fails,
     *    because at that point there genuinely is no credential left.
     */
    async clientInit() {
      if (typeof window === 'undefined') return;

      const msal = (this as any).$msal || (window as any)?.msal;
      // No session at all is somebody else's decision (serverInit for a global
      // channel, the Login button elsewhere) — never redirect from here.
      if (!msal?.isAuthenticated()) return;

      const hasValidToken = typeof msal.hasValidToken === 'function' ? msal.hasValidToken() : !!readTokenCookie();

      if (hasValidToken) {
        const token = readTokenCookie();
        if (token) {
          this.token = token;
          this.hasToken = true;
        }
        // Only re-ask when the current answer was not produced by this token.
        // A confirmed `isInternal === false` is a final answer: the user is signed
        // in and simply has no internal access. Nothing to redirect about.
        if (this.statusToken !== token) {
          const internal = await this.refreshStatus();
          if (internal) await this.loadCategories();
        }
        return;
      }

      // Authenticated per the MSAL account cache, but no usable token.
      if (typeof msal.silentLogin === 'function') {
        try {
          const ok = await msal.silentLogin({ scopes: ['openid', 'profile', 'email'] });
          if (ok) {
            const refreshed = readTokenCookie();
            if (refreshed) {
              this.token = refreshed;
              this.hasToken = true;
              await this.refreshStatus();
              await this.loadCategories();
              return;
            }
            // silentLogin reported success but no usable token materialised —
            // don't leave the user stranded; fall through to interactive redirect.
          }
        } catch {
          // fall through to interactive redirect
        }
      }
      saveRedirectLocation();
      msal.loginRedirect({ scopes: ['user.read'] });
    },
    /**
     * Load categories from the API and optionally resolve subcategoryData if route params are present.
     * Can be called independently whenever the token changes (e.g., after login).
     * @param {{ params?: RouteParams }} payload
     */
    async loadCategories(payload: { params?: RouteParams } = {}) {
      const { params = {} } = payload;
      // Resolve token from store or cookie
      let token = this.token || '';
      if (!token && typeof document !== 'undefined') {
        const m = document.cookie.match(/(?:^|;\s*)token=([^;]+)/);
        token = m ? decodeURIComponent(m[1]) : '';
      }
      const ServerInitService = new ServerInit(this);
      ServerInitService.setToken(token);
      const categories = await ServerInitService.getCategories();
      this.categories = Array.isArray(categories) ? categories : [];
      // Optionally set subcategoryData if params are present (as in old logic)
      if (params.category && params.subcategory) {
        const categoryIndex = this.categories.findIndex((item) => item.nodeId == params.category);
        if (categoryIndex > -1 && this.categories[categoryIndex].nodes) {
          const subCategoryIndex = this.categories[categoryIndex].nodes!.findIndex((item) => item.nodeId == params.subcategory);
          if (subCategoryIndex > -1) {
            const subcategory = this.categories[categoryIndex].nodes![subCategoryIndex];
            this.subcategoryData = subcategory;
          }
        }
      }
    },
    /**
     * Re-fetch the internal status (isInternal) using the current token.
     * Call this after a silent token refresh: otherwise isInternal stays false
     * from the token-less load that happened while the token was expired, and
     * gated content keeps being hidden even though a valid token now exists.
     */
    async refreshStatus(force = false): Promise<boolean> {
      const token = this.token || readTokenCookie();
      // The answer depends only on the token we present, so re-asking with the same
      // token can only ever produce the same answer. Callers use this to distinguish
      // "confirmed not internal" from "never asked with this token".
      if (!force && this.statusToken === token) {
        return this.isInternal;
      }
      try {
        const ServerInitService = new ServerInit(this);
        ServerInitService.setToken(token);
        const status = await ServerInitService.getStatus();
        this.isInternal = !!(status as any)?.isInternal;
        this.statusToken = token;
        return this.isInternal;
      } catch (e) {
        // Return false so callers gate their "internal content ready" reveal on a
        // confirmed refresh rather than assuming success. Leave `isInternal` itself
        // untouched — a transient status error shouldn't blank an internal user.
        console.error('refreshStatus failed', e);
        return false;
      }
    },
    /**
     * Load subcategory data (nodes/resources) by params and store in subcategoryData.
     * Mirrors legacy setSubcategoryData for downloads/products parent routes.
     * @param {{ params: any }} payload
     */
    async setSubcategoryData(payload: SubcategoryPayload = { params: {} }) {
      const { params } = payload;
      if (!params || !params.subcategory) return;
      // Ensure token is available
      let token = this.token || '';
      if (!token && typeof document !== 'undefined') {
        const m = document.cookie.match(/(?:^|;\s*)token=([^;]+)/);
        token = m ? decodeURIComponent(m[1]) : '';
      }
      try {
        const svc = new Subcategory(this, { token }, params.subcategory);
        const data = await svc.getSubcategory();
        // Normalize response
        const sub = data?.data ?? data;
        if (sub) this.subcategoryData = sub;
      } catch (e) {
        console.error('setSubcategoryData failed', e);
        this.subcategoryData = {};
      }
    },
    /**
     * Load subcategory tab data for products/downloads pages.
     * Mirrors legacy Vuex action but uses fetch/$fetch services.
     * @param {{ params: any, checkData?: { check: boolean } }} payload
     */
    async setSubcategoryTabData(payload: SubcategoryTabPayload = { params: {}, checkData: { check: false } }) {
      const { params, checkData } = payload;
      // Skip if requested and data already present
      if (checkData?.check && Array.isArray(this.subcategoryTabData) && this.subcategoryTabData.length > 0) return;

      // Ensure we have token when available
      let token = '';
      if (params && params.token) {
        token = params.token;
      } else if (this.token) {
        token = this.token;
      } else if (typeof document !== 'undefined') {
        const m = document.cookie.match(/(?:^|;\s*)token=([^;]+)/);
        token = m ? decodeURIComponent(m[1]) : '';
      }

      // Build and call service
      const svc = new SubcategoryTab(this, {
        subcategoryTab: params?.subcategoryTab,
        channelNodes: this.channelNodes || '',
        token
      });
      try {
        const data = await svc.getSubcategoryTab();
        // $fetch returns parsed JSON directly
        this.subcategoryTabData = Array.isArray(data) ? data : (data?.data ?? []);
      } catch (e) {
        // Surface but don’t crash rendering
        console.error('setSubcategoryTabData failed', e);
        this.subcategoryTabData = [];
      }
    },
    /**
     * Calculate and store downloads page data (transformed nodes) for a subcategory.
     * @param {{ params: any }} payload
     */
    async setDownloadsPageData(payload: SubcategoryPayload = { params: {} }) {
      const { params } = payload;
      try {
        // Legacy-first logic: use already loaded categories to derive subcategory
        // Accept optional routeName for parity with Nuxt 2 checks
        const routeName = (params as any)?.routeName || '';
        if (params?.subcategory && (routeName ? String(routeName).includes('downloads-category') : true)) {
          const categories = Array.isArray(this.categories) ? this.categories : [];
          const categoryIndex = categories.findIndex((c) => String(c.nodeId) == String(params.category));
          if (categoryIndex > -1 && Array.isArray(categories[categoryIndex].nodes)) {
            const subCategoryIndex = categories[categoryIndex].nodes!.findIndex((n) => String(n.nodeId) == String(params.subcategory));
            if (subCategoryIndex > -1) {
              const subcategory = categories[categoryIndex].nodes![subCategoryIndex];
              // Mirror old behavior
              await this.setDownloadsSubcategoryData(subcategory);
              this.setDownloadsSubcategoryTabData({ subcategory, params });
            }
          }
        }

        // Ensure subcategory data is available (fallback to API if missing)
        if (!this.subcategoryData || !Array.isArray(this.subcategoryData.nodes)) {
          await this.setSubcategoryData({ params });
        }
        const sub = this.subcategoryData || {};
        const nodes = Array.isArray(sub.nodes) ? sub.nodes : [];
        if (!nodes.length) {
          this.downloadsPageData = [];
          return;
        }
        // Build transformed array similar to page logic
        const transformedArr = [];
        const productCard = { items: [], productBlockType: 'ProductCard' };
        const productOverview = { items: [], productBlockType: 'ProductOverview' };
        for (const block of nodes) {
          if (block.productBlockType === 'ProductOverview') {
            (productOverview.items as any[]).push(block);
          } else if (block.productBlockType === 'ProductCard') {
            (productCard.items as any[]).push(block);
          } else {
            transformedArr.push(block);
          }
        }
        if (productCard.items.length) transformedArr.push(productCard);
        if (productOverview.items.length) transformedArr.push(productOverview);
        this.downloadsPageData = transformedArr;
      } catch (e) {
        console.error('setDownloadsPageData failed', e);
        this.downloadsPageData = [];
      }
    },
    // Helpers to mirror legacy behavior for downloads pages
    async setDownloadsSubcategoryData(subcategory: Category) {
      // Mirror Nuxt 2 logic: if subcategory has exactly one node where id == nodeId,
      // fetch channel/{lang}/{nodeId} and replace nodes with API response.
      try {
        if (subcategory && subcategory.external && Array.isArray(subcategory.nodes) && subcategory.nodes.length === 1) {
          const first = subcategory.nodes[0];
          if (first && String(first.id) === String(first.nodeId)) {
            // Resolve token from store or cookie
            let token = this.token || '';
            if (!token && typeof document !== 'undefined') {
              const m = document.cookie.match(/(?:^|;\s*)token=([^;]+)/);
              token = m ? decodeURIComponent(m[1]) : '';
            }
            const headers = token ? { Authorization: `Bearer ${token}` } : undefined;
            const base = this.baseUrlApiGetter || this.baseUrlApi || '';
            const url = `${base}channel/${this.lang}/${first.nodeId}`;
            const data = await $fetch(url, { headers });
            this.subcategoryData = {
              external: subcategory.external,
              nodes: Array.isArray(data) ? data : ((data as any)?.data ?? [])
            };
            return;
          }
        }
        // Fallback: set provided subcategory directly
        this.subcategoryData = subcategory || {};
      } catch (err) {
        console.error('setDownloadsSubcategoryData failed', err);
        this.subcategoryData = subcategory || {};
      }
    },
    setDownloadsSubcategoryTabData(
      payload: { subcategory: Category | null; params: RouteParams } = {
        subcategory: null,
        params: {}
      }
    ) {
      const { subcategory, params } = payload;
      // Pre-compute channelNodes for a given subcategoryTab if present (used by downstream tab data loads)
      if (!subcategory || !Array.isArray(subcategory.nodes) || !params?.subcategoryTab) return;
      const tab = subcategory.nodes.find(
        (t) => String(t.nodeId) === String(params.subcategoryTab) || String(t.id) === String(params.subcategoryTab)
      );
      if (tab && Array.isArray(tab.nodes) && tab.nodes.length) {
        const ids = tab.nodes
          .map((n) => n.id ?? n.nodeId)
          .filter(Boolean)
          .join(',');
        this.channelNodes = ids;
      }
    },
    setTabChangeState(val: boolean) {
      this.tabChangeState = !!val;
      if (tabChangeResetTimer) {
        clearTimeout(tabChangeResetTimer);
        tabChangeResetTimer = null;
      }
      if (this.tabChangeState) {
        tabChangeResetTimer = setTimeout(() => {
          this.tabChangeState = false;
          tabChangeResetTimer = null;
        }, 800);
      }
    },
    setBreadcrumbs(items: any[]) {
      this.breadcrumbs = items;
    },
    /**
     * Centralized scroll lock management for modals/overlays
     * Prevents conflicts when multiple components try to manage scroll
     */
    lockBodyScroll() {
      if (typeof document === 'undefined') return;

      // Initialize scrollLockState if it doesn't exist (defensive programming)
      if (!this.scrollLockState) {
        this.scrollLockState = {
          isLocked: false,
          originalScrollY: 0,
          originalHtmlOverflow: '',
          originalBodyOverflow: '',
          originalBodyPaddingRight: '',
          originalNavigationPaddingRight: '',
          lockCount: 0,
          isScrollOperationInProgress: false
        };
      }

      this.scrollLockState.lockCount++;

      // Only apply lock if this is the first request
      if (this.scrollLockState.lockCount === 1 && !this.scrollLockState.isLocked) {
        const body = document.body;
        const html = document.documentElement;
        const navigation = document.getElementById('navigation__inner');
        const scrollBarWidth = window.innerWidth - html.clientWidth;

        // Store original values
        this.scrollLockState.originalScrollY = window.scrollY || window.pageYOffset || 0;
        this.scrollLockState.originalHtmlOverflow = html.style.overflow || '';
        this.scrollLockState.originalBodyOverflow = body.style.overflow || '';
        this.scrollLockState.originalBodyPaddingRight = body.style.paddingRight || '';
        this.scrollLockState.originalNavigationPaddingRight = navigation ? navigation.style.paddingRight || '' : '';

        // Apply scroll lock while maintaining scrollbar space
        html.style.overflow = 'hidden';
        body.style.overflow = 'hidden';
        if (scrollBarWidth > 0) {
          body.style.paddingRight = `${scrollBarWidth}px`;
          // Also apply padding to navigation to prevent shifting
          if (navigation) {
            navigation.style.paddingRight = `${scrollBarWidth}px`;
          }
        }

        this.scrollLockState.isLocked = true;
      }
    },
    /**
     * Unlock body scroll - only unlocks when all lock requests are released
     */
    unlockBodyScroll() {
      if (typeof document === 'undefined') return;

      // Initialize scrollLockState if it doesn't exist (defensive programming)
      if (!this.scrollLockState) {
        this.scrollLockState = {
          isLocked: false,
          originalScrollY: 0,
          originalHtmlOverflow: '',
          originalBodyOverflow: '',
          originalBodyPaddingRight: '',
          originalNavigationPaddingRight: '',
          lockCount: 0,
          isScrollOperationInProgress: false
        };
        return; // Nothing to unlock if it wasn't initialized
      }

      this.scrollLockState.lockCount = Math.max(0, this.scrollLockState.lockCount - 1);

      // Only remove lock when no more lock requests exist
      if (this.scrollLockState.lockCount === 0 && this.scrollLockState.isLocked) {
        const body = document.body;
        const html = document.documentElement;
        const navigation = document.getElementById('navigation__inner');

        // Restore original styles
        html.style.overflow = this.scrollLockState.originalHtmlOverflow;
        body.style.overflow = this.scrollLockState.originalBodyOverflow;
        body.style.paddingRight = this.scrollLockState.originalBodyPaddingRight;

        // Restore navigation padding
        if (navigation) {
          navigation.style.paddingRight = this.scrollLockState.originalNavigationPaddingRight;
        }

        // Restore scroll position
        this.scrollLockState.isScrollOperationInProgress = true;

        // Use requestAnimationFrame to ensure DOM is ready for scroll restoration
        requestAnimationFrame(() => {
          window.scrollTo(0, this.scrollLockState.originalScrollY);

          // Clear the scroll operation flag after a short delay
          setTimeout(() => {
            this.scrollLockState.isScrollOperationInProgress = false;
          }, 100);
        });

        this.scrollLockState.isLocked = false;
      }
    },
    /**
     * Force unlock scroll (emergency cleanup)
     */
    forceUnlockBodyScroll() {
      if (typeof document === 'undefined') return;

      // Initialize scrollLockState if it doesn't exist (defensive programming)
      if (!this.scrollLockState) {
        this.scrollLockState = {
          isLocked: false,
          originalScrollY: 0,
          originalHtmlOverflow: '',
          originalBodyOverflow: '',
          originalBodyPaddingRight: '',
          originalNavigationPaddingRight: '',
          lockCount: 0,
          isScrollOperationInProgress: false
        };
        return; // Nothing to unlock if it wasn't initialized
      }

      this.scrollLockState.lockCount = 0;
      if (this.scrollLockState.isLocked) {
        this.unlockBodyScroll();
      }
    },
    /**
     * Open product details popup with the provided resource
     * @param {Object} resource - The resource object to display in ProductDetails
     */
    openProductDetails(resource: any) {
      this.resource = resource;
      this.productDetailsState = true;
      this.lockBodyScroll();
    },
    /**
     * Close product details popup
     */
    closeProductDetails() {
      this.productDetailsState = false;
      this.resource = null;
      this.unlockBodyScroll();
    },
    /**
     * Set scroll operation in progress flag
     */
    setScrollOperationInProgress(isInProgress: boolean) {
      if (!this.scrollLockState) {
        this.scrollLockState = {
          isLocked: false,
          originalScrollY: 0,
          originalHtmlOverflow: '',
          originalBodyOverflow: '',
          originalBodyPaddingRight: '',
          originalNavigationPaddingRight: '',
          lockCount: 0,
          isScrollOperationInProgress: false
        };
      }
      this.scrollLockState.isScrollOperationInProgress = !!isInProgress;
    }
  }
});

export { useMainStore };
